/**
 * Save orchestration for the Binom drawer — wraps useUpdateBinomFlow with a
 * confirm dialog + applying state and returns the 1-or-3 footer actions.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  useUpdateBinomFlow,
  type BinomFlowUpdateBody,
} from '@/hooks/binom';

import type { BinomFlowDraft } from './binom-flow-draft.types';
import { draftToUpdateBody } from './draft-to-update-body';
import {
  buildBinomSaveActions,
  targetConfirmMessage,
} from './binom-save-actions';

interface UseBinomDrawerSaveParams {
  flowId: string;
  campaignId?: string;
  draft: BinomFlowDraft | null;
  dirty: boolean;
  killSwitch: boolean;
  flowQuery: UseQueryResult<unknown>;
  campaignQuery: UseQueryResult<unknown>;
}

export function useBinomDrawerSave(params: UseBinomDrawerSaveParams) {
  const { flowId, campaignId, draft, dirty, killSwitch, flowQuery, campaignQuery } = params;
  const updateMutation = useUpdateBinomFlow(flowId, campaignId);
  const [applying, setApplying] = useState(false);

  const save = useCallback(
    async (body: BinomFlowUpdateBody) => {
      const target = body.target ?? 'rotation';
      if (!window.confirm(targetConfirmMessage(target))) return;
      setApplying(true);
      try {
        await updateMutation.mutateAsync(body);
        flowQuery.refetch();
        campaignQuery.refetch();
      } finally {
        setApplying(false);
      }
    },
    [updateMutation, flowQuery, campaignQuery],
  );

  const actions = useMemo(() => {
    if (!draft) return [];
    return buildBinomSaveActions({
      flowId,
      campaignId,
      baseBody: draftToUpdateBody(draft),
      disabled: !dirty || applying || killSwitch,
      save,
    });
  }, [draft, dirty, flowId, campaignId, applying, killSwitch, save]);

  return { applying, actions };
}
